import { Box, Stack, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ValuationHubCoreModelDtoValuationReportConfigurationDto } from "@/api/generatedApi";
import { FormControlTextField } from "@/components/formControls";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  mainContainer: {
    display: "flex",
    gap: theme.analysisLayout.panelGap,
    flexDirection: "column",
  },
  formInput: {
    backgroundColor: "white",
    width: "100%",
  },
  textFieldInput: {
    "& .MuiInputBase-root:not(.MuiInputBase-multiline)": {
      height: theme.analysisLayout.inputHeight,
    },
  },
  inputsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    columnGap: theme.analysisLayout.panelGap,
    rowGap: theme.analysisLayout.compactGap,
    width: "100%",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
  },
  splitFieldGroup: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.analysisLayout.compactGap,
    width: "100%",
  },
});

export const ContactInformationSection = (): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { t } = useTranslation("ValuationReportGenerationPanel");
  const { control } = useFormContext<ValuationHubCoreModelDtoValuationReportConfigurationDto>();

  return (
    <Box sx={styles.mainContainer}>
      <Stack alignItems={"start"} direction="column" spacing="6px" sx={{ width: "100%" }}>
        <Typography variant={"h4"}>{t("reportPanel.configuration.contactInformation")}</Typography>
        <Typography variant={"body1"}>{t("reportPanel.configuration.contactInformationDescription")}</Typography>
      </Stack>
      <Box sx={styles.inputsGrid}>
        <Box sx={styles.fieldGroup}>
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactCompanyName")}</Typography>
          <FormControlTextField
            name="contact_information.company_name"
            control={control}
            sx={styles.formInput}
            textFieldSx={styles.textFieldInput}
            inputProps={{ maxLength: 100 }}
            data-cy="report-configuration-company-name"
          />
        </Box>
        <Box sx={styles.fieldGroup}>
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactEmail")}</Typography>
          <FormControlTextField
            name="contact_information.email"
            control={control}
            sx={styles.formInput}
            textFieldSx={styles.textFieldInput}
            inputProps={{ maxLength: 100 }}
            data-cy="report-configuration-email"
          />
        </Box>
        <Box sx={styles.fieldGroup}>
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactName")}</Typography>
          <FormControlTextField
            name="contact_information.name"
            control={control}
            sx={styles.formInput}
            textFieldSx={styles.textFieldInput}
            inputProps={{ maxLength: 100 }}
            data-cy="report-configuration-name"
          />
        </Box>
        <Box sx={styles.fieldGroup}>
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactPhoneNumber")}</Typography>
          <FormControlTextField
            name="contact_information.phone_number"
            control={control}
            sx={styles.formInput}
            textFieldSx={styles.textFieldInput}
            inputProps={{ maxLength: 100 }}
            data-cy="report-configuration-phone-number"
          />
        </Box>
        <Box sx={styles.splitFieldGroup}>
          <Box sx={styles.fieldGroup}>
            <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactStreet")}</Typography>
            <FormControlTextField
              name="contact_information.street"
              control={control}
              sx={styles.formInput}
              textFieldSx={styles.textFieldInput}
              inputProps={{ maxLength: 100 }}
              data-cy="report-configuration-street"
            />
          </Box>
          <Box sx={styles.fieldGroup}>
            <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactHouseNumber")}</Typography>
            <FormControlTextField
              name="contact_information.house_number"
              control={control}
              sx={styles.formInput}
              textFieldSx={styles.textFieldInput}
              inputProps={{ maxLength: 100 }}
              data-cy="report-configuration-house-number"
            />
          </Box>
        </Box>
        <Box sx={styles.fieldGroup}>
          <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactWebsite")}</Typography>
          <FormControlTextField
            name="contact_information.website"
            control={control}
            sx={styles.formInput}
            textFieldSx={styles.textFieldInput}
            inputProps={{ maxLength: 100 }}
            data-cy="report-configuration-website"
          />
        </Box>

        <Box sx={styles.splitFieldGroup}>
          <Box sx={styles.fieldGroup}>
            <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactPostCode")}</Typography>
            <FormControlTextField
              name="contact_information.post_code"
              control={control}
              sx={styles.formInput}
              textFieldSx={styles.textFieldInput}
              inputProps={{ maxLength: 100 }}
              data-cy="report-configuration-post-code"
            />
          </Box>
          <Box sx={styles.fieldGroup}>
            <Typography variant={"inputLabel"}>{t("reportPanel.configuration.contactCity")}</Typography>
            <FormControlTextField
              name="contact_information.town"
              control={control}
              sx={styles.formInput}
              textFieldSx={styles.textFieldInput}
              inputProps={{ maxLength: 100 }}
              data-cy="report-configuration-town"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
